import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, FileMeta } from '../lib/api';
import { absoluteTime, bytes, shortHash } from '../lib/format';
import { ErrorState } from '../components/QueryState';

export function Files() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['files'],
    queryFn: () => api.get<{ files: FileMeta[] }>('/files/'),
  });

  const [path, setPath] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [replace, setReplace] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: () => {
      if (!file || !path) throw new Error('select a file and target path');
      const normalized = path.replace(/^\/+/, '');
      if (!normalized || normalized.includes('..') || normalized.split('/').some((s) => !s)) {
        throw new Error('path must be a relative path with no .. or empty segments');
      }
      const url = replace ? '/files/?replace=1' : '/files/';
      return api.upload<FileMeta>(url, file, { path: normalized });
    },
    onSuccess: () => {
      setErr(null);
      setPath('');
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      qc.invalidateQueries({ queryKey: ['files'] });
      qc.invalidateQueries({ queryKey: ['manifest', 'global'] });
    },
    onError: (e: Error & { details?: { error?: string; hint?: string } }) =>
      setErr((e.details?.error || e.message) + (e.details?.hint ? ` (${e.details.hint})` : '')),
  });

  const del = useMutation({
    mutationFn: (f: FileMeta) => api.delete(`/files/${f.id}`),
    onSuccess: () => {
      setDelErr(null);
      qc.invalidateQueries({ queryKey: ['files'] });
      qc.invalidateQueries({ queryKey: ['manifest', 'global'] });
    },
    onError: (e: Error & { details?: { referenced_by?: string[] } }) =>
      setDelErr(
        e.details?.referenced_by
          ? `Cannot delete: still referenced by ${e.details.referenced_by.join(', ')}. Remove from those manifests first.`
          : e.message
      ),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Files</h1>
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      <section className="card space-y-2">
        <h2 className="font-semibold">Upload</h2>
        <p className="text-sm text-muted">
          Files are stored in the database. The <em>logical path</em> (e.g. <code>etc/ssl/ca.pem</code>) is what manifest entries reference as <code>src</code>.
        </p>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-sm flex flex-col gap-1">
            <span className="text-muted">Logical path</span>
            <input className="input w-72" value={path} onChange={(e) => setPath(e.target.value)} placeholder="etc/ssl/ca.pem" />
          </label>
          <label className="text-sm flex flex-col gap-1">
            <span className="text-muted">File</span>
            <input ref={fileInput} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
            Replace if exists
          </label>
          <button className="btn btn-primary" disabled={upload.isPending || !file || !path} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {err && <div className="text-danger text-sm">{err}</div>}
      </section>

      <section className="card overflow-auto">
        <h2 className="font-semibold mb-3">Uploaded files</h2>
        {delErr && <div className="text-danger text-sm mb-2">{delErr}</div>}
        <table className="table">
          <thead>
            <tr><th>Path</th><th>Size</th><th>sha256</th><th>Mime</th><th>Uploaded</th><th></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-muted">Loading…</td></tr>}
            {data?.files.length === 0 && (
              <tr><td colSpan={6} className="text-muted">No files uploaded yet.</td></tr>
            )}
            {data?.files.map((f) => (
              <tr key={f.id}>
                <td className="font-mono text-xs">{f.path}</td>
                <td>{bytes(f.size)}</td>
                <td className="font-mono text-xs">{shortHash(f.sha256)}</td>
                <td className="text-xs">{f.mime || '—'}</td>
                <td className="text-xs">{absoluteTime(f.uploaded_at)}</td>
                <td className="space-x-2 whitespace-nowrap">
                  <a className="btn" href={`/admin/api/files/${f.id}?download=1`} target="_blank" rel="noreferrer">Download</a>
                  <button className="btn btn-danger" onClick={() => {
                    if (confirm(`Delete ${f.path}?`)) del.mutate(f);
                  }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
